import {
  TextField,
  Box,
  Typography,
  Divider,
  Stack,
  lighten,
  darken,
  useTheme,
} from "@mui/material";
import { RegisterOptions, useFormContext } from "react-hook-form";
import React from "react";
import { InputError } from "./FormComponents";
import { formatUSPhoneNumber, formatPhoneNumber } from "src/utils/utils";

interface Props {
  fieldName?: string;
  defaultValue?: string;
}

export function PhoneNumberTextField(props: {
  fieldName?: string;
  name?: string;
  placeholder?: string;
  defaultValue?: string;
  options?: RegisterOptions;
}) {
  const theme = useTheme();

  const fieldName = props?.fieldName || props?.name || "phoneNumber";
  const placeholder = props?.placeholder || "Phone Number";
  const defaultValue = props?.defaultValue || "";

  const {
    setValue,
    getValues,
    clearErrors,
    register,
    formState: { errors },
    unregister,
  } = useFormContext();

  const errorMessage = errors[fieldName]?.message as string;
  const [phoneNumber, setPhoneNumber] = React.useState(
    formatUSPhoneNumber(defaultValue)
  );
  const [warningMessage, setWarningMessage] = React.useState("");

  const handleChange = (ev: any) => {
    let value = ev.target.value;

    // fixing autofill issue
    if (
      ev.inputType === "insertReplacementText" ||
      ev.target.matches(":-webkit-autofill") ||
      (ev.inputType == undefined && ev.target.value.length >= 10)
    ) {
      const formattedNumber = formatUSPhoneNumber(value);
      setPhoneNumber(formattedNumber);
      return;
    }

    const isNumber = /[0-9]/.test(ev.nativeEvent.data);
    const isBackspace = ev.nativeEvent.inputType === "deleteContentBackward";
    if (isBackspace) setPhoneNumber(value);
    if (isNumber && value.length <= 12) {
      setPhoneNumber(formatPhoneNumber(value));
    }
    clearErrors(fieldName);
    if (!isNumber || !isBackspace) return;
  };

  // registering to hook form ctx
  React.useEffect(() => {
    register(fieldName, {
      required: "Phone Number is required",
      minLength: {
        value: 10,
        message: "",
      },
      maxLength: {
        value: 10,
        message: "",
      },
      ...props?.options,
    });
  }, [register]);

  // setting value to hook form ctx and showing warning message
  React.useEffect(() => {
    const value = phoneNumber.replaceAll("-", "");
    setValue(fieldName, value);
    let timeout = setTimeout(() => {
      if ((value?.length < 10 && value?.length > 0) || value?.length > 10) {
        setWarningMessage("Phone Number should have 10 digits");
      } else {
        setWarningMessage("");
      }
      clearTimeout(timeout);
    }, 300);
  }, [phoneNumber]);

  React.useEffect(() => {
    return () => {
      unregister?.(fieldName);
    };
  }, []);

  return (
    <Box>
      {/* <input
        name="phoneNumber"
        onChange={(ev) => {
          console.log(ev.target.value);
        }}
      ></input> */}

      <TextField
        name={fieldName}
        size="small"
        fullWidth
        placeholder={placeholder}
        // onChange={handleChange}
        value={phoneNumber}
        InputProps={{
          onChange: handleChange,
          startAdornment: (
            <Stack direction={"row"}>
              <Box>
                <Typography color={theme.palette.divider}>+1</Typography>
              </Box>
              <Box px={1}>
                <Divider orientation="vertical" />
              </Box>
            </Stack>
          ),
        }}
      />

      <Box
        p={warningMessage ? 1 : 0}
        mt={warningMessage ? 1 : 0}
        bgcolor={lighten(theme.palette.warning.main, 0.9)}
        borderRadius={"4px"}
        width={"100%"}
        height={warningMessage ? "24px" : "0px"}
        sx={{
          transition: "all 0.2s ease-in-out",
          opacity: warningMessage ? 1 : 0,
        }}
        display={"flex"}
        alignItems={"center"}
        justifyContent={"left"}
      >
        <Typography
          variant="body2"
          color={darken(theme.palette.warning.main, 0.4)}
        >
          {warningMessage}
        </Typography>
      </Box>

      <Box>
        <InputError>{errorMessage}</InputError>
      </Box>
    </Box>
  );
}
